import { Stack, Typography } from '@mui/material'
import { FeatureFlagIdProvisioningDrawer, FeatureFlagIdGrantReasonsManualMembership } from 'c1gen/features'
import { C1Button } from 'c1ui/components/C1Button'
import { SimpleConfirmationDialog } from 'components/core/confirmation/dialog/SimpleConfirmationDialog'
import { EmptyList } from 'components/core/empty-list'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { QueryFilter } from 'components/core/search-filter/query'
import RequestAccessFormV2Dialog from 'components/core/simple-entitlement-request-dialog/RequestAccessFormV2Dialog'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { RelativeTimestamp } from 'components/core/time/display'
import { HasFeature, useFeatureChecker } from 'components/no-ui/capability'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { GrantTaskType } from 'components/pages/task/type'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { useAlert } from 'hooks/useAlert'
import { CatalogEntryValue, AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { TaskValue } from 'lib/actions/task'
import { C1AppId } from 'lib/constants/system'
import { useSimpleModal } from 'lib/modals'
import { useInitialFilters, useSearch } from 'lib/search'
import { getRefString } from 'lib/websocket-notify'
import {
    SearchAppEntitlementsWithExpiredRequest,
    AppEntitlementWithExpired,
} from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { ForceRunBundleAutomationRequest } from 'pbts/c1/api/requestcatalog/v1/requestcatalogmanagement'
import { TaskSearchRequest } from 'pbts/c1/api/task/v1/search'
import { TaskState } from 'pbts/c1/api/task/v1/task'
import { UserRef } from 'pbts/c1/api/user/v1/user'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchAppEntitlementsWithExpiredApi } from 'redux_toolkit/entitlements'
import {
    forceRunBundleAutomationApi,
} from 'redux_toolkit/request-catalogs'
import { tasksSearchApi } from 'redux_toolkit/tasks'

type EnrollmentStatus = {
    appEntitlementId?: string
    expired?: Timestamp
}

type ProfileEnrollmentAppUsersWithTasksAndExpires = AppEntitlementWithExpired & {
    status?: EnrollmentStatus
    grant?: TaskValue
}

type InjectOpenProvisionDialog = (row: ProfileEnrollmentAppUsersWithTasksAndExpires) => void

const showRequestButton = (row: ProfileEnrollmentAppUsersWithTasksAndExpires): boolean => (
    !row.status?.appEntitlementId && !row.grant
)

const Tickets: FC<{ row: ProfileEnrollmentAppUsersWithTasksAndExpires }> = ({
    row,
}) => (
    <Stack direction="row" spacing={1}>
        {row.grant && <TaskViewLinkV2 task={row.grant} />}
    </Stack>
)

const ProvisioningStatus: FC<{ row: ProfileEnrollmentAppUsersWithTasksAndExpires }> = ({
    row,
}) => (
    row.status?.expired ? <RelativeTimestamp
        date={row.status.expired}
        variant="default"
        typographyVariant="body1"
    /> : (row.status?.appEntitlementId ? <Typography>
        <FormattedMessage id="components_user_list_permanent" defaultMessage="Permanent" />
    </Typography>
        : (row.grant) ? <Tickets row={row} />
            : <Typography variant="unconfigured">
                <FormattedMessage id="components_user_list_not_granted" defaultMessage="Not granted" />
            </Typography>
    )
)

const cols: C1TableReduxColumn<ProfileEnrollmentAppUsersWithTasksAndExpires, InjectOpenProvisionDialog>[] = [
    {
        field: 'user',
        flex: 2,
        label: <FormattedMessage id="components_app_entitlement_user_list_identity_user" defaultMessage="User" />,
        Component: ({ row }) => (
            row.user
                ? <UserDisplayName user={row.user} showTooltip showUserStatus />
                : <Typography variant="unconfigured">
                    <FormattedMessage id="components_app_entitlement_user_list_no_c1_user" defaultMessage="Not assigned" />
                </Typography>
        ),
    },
    {
        field: 'status',
        label: <FormattedMessage id="components_app_entitlement_user_list_status" defaultMessage="Status" />,
        Component: ({ row }) => <ProvisioningStatus row={row} />,
    },
    {
        field: 'action',
        flex: 0,
        label: <></>,
        Component: ({ row, extras: openProvisionDialog }): React.ReactElement => (showRequestButton(row) ? <C1Button
            color="info"
            variant="outlined"
            onClick={(): void => {
                openProvisionDialog(row)
            }}
        >
            <FormattedMessage id="components_app_entitlement_user_list_request" defaultMessage="Request" />
        </C1Button> : <></>),
    },
]

type ManageProvisioningEntitlementsListProps = {
    catalogId: string
    appEntitlementValue: AppEntitlementValue
    showPending?: boolean
}

const urlEncodeUserRef: (k: string, v: unknown) => string | string[] = (key, value) => (
    key === 'userRefs' && Array.isArray(value)
        ? value.map((v) => getRefString(v))
        : value as string
)

const pageSize = 25

export const ManageProvisioningEntitlementsList: FC<ManageProvisioningEntitlementsListProps> = ({
    catalogId,
    appEntitlementValue,
}) => {
    const hasProvisioningDrawerFF = useFeatureChecker(FeatureFlagIdProvisioningDrawer)

    const intl = useIntl()
    const toast = useAlert()

    const { mutate: runBundleAutomation, inProgress: forceRunInProgress } = forceRunBundleAutomationApi()

    const { open } = useSimpleModal()

    const onProvisionAllEntitlement = useCallback(async (entitlement: CatalogEntryValue): Promise<void> => {
        await runBundleAutomation(ForceRunBundleAutomationRequest.create({
            requestCatalogId: catalogId,
            refs: [AppEntitlementRef.create({ id: entitlement.id, appId: entitlement.appId })],
        }))

        toast(
            intl.formatMessage({
                id: 'success_request_catalog_run_automation_provision',
                defaultMessage: 'Provision queued for execution',
            }),
            'success',
        )
    }, [catalogId, intl, runBundleAutomation, toast])

    const onProvisionAllClick = useCallback(async (entitlement: CatalogEntryValue): Promise<boolean> => (
        open(SimpleConfirmationDialog, {
            onConfirm: async () => onProvisionAllEntitlement(entitlement),
            title: intl.formatMessage({
                defaultMessage: 'Provision entitlement to enrolled users',
                id: 'components_request_catalog_entitlement_list_provision_all_title',
            }),
            details: <>
                <Typography mb={2}>
                    <FormattedMessage id="components_request_catalog_entitlement_list_provision_all_details" defaultMessage="This action will create requests for enrolled users who are not currently granted this entitlement:" />
                </Typography>
                <C1EntitlementDisplayName entitlement={entitlement} />
            </>,
            inProgress: forceRunInProgress,
        })()
    ), [intl, open, forceRunInProgress, onProvisionAllEntitlement])

    const onProvisionClick = useCallback(async (row: ProfileEnrollmentAppUsersWithTasksAndExpires): Promise<boolean> => {
        if (!row.user) {
            return false
        }

        return open(RequestAccessFormV2Dialog, {
            entitlements: [appEntitlementValue],
            user: row.user,
        })()
    }, [appEntitlementValue, open])

    const initialFilters = useInitialFilters(
        SearchAppEntitlementsWithExpiredRequest,
        [],
        undefined,
        undefined,
        ['query'],
    )

    const emptyReq = useMemo<Partial<SearchAppEntitlementsWithExpiredRequest>>(
        () => (
            SearchAppEntitlementsWithExpiredRequest.create({
                appId: C1AppId,
                appEntitlementId: catalogId,
            })
        ),
        [catalogId],
    )

    const [filter, setFilter] = useState<SearchAppEntitlementsWithExpiredRequest>(
        SearchAppEntitlementsWithExpiredRequest.create({
            ...initialFilters,
            ...emptyReq,
        }),
    )

    const { onQueryChange } = useSearch(
        SearchAppEntitlementsWithExpiredRequest,
        filter,
        setFilter,
        emptyReq,
        urlEncodeUserRef,
    )

    const search = useMemo(() => <QueryFilter
        autoFocus
        query={filter?.query || ''}
        setQuery={(q): void => onQueryChange(q as unknown as string, false)}
        onChange={onQueryChange}
    />, [filter, onQueryChange])

    const [page, setPage] = useState<number>(0)

    const {
        data: membershipData,
        isLoading: membershipIsLoading,
        isError: membershipIsError,
        error: membershipError,
        getPage,
        canLoadMore,
        shouldInvalidateList,
    } = searchAppEntitlementsWithExpiredApi.useSearchAppEntitlementsWithExpired(SearchAppEntitlementsWithExpiredRequest.create({
        appId: C1AppId,
        appEntitlementId: catalogId,
        query: filter.query,
    }), {
        pageSize,
        notify: true,
        autoReload: true,
        actionable: !!hasProvisioningDrawerFF,
    })

    const currentMembershipPage = membershipData[page] || []
    const hasMembershipUsers = currentMembershipPage.length > 0
    const userIds = currentMembershipPage.map((v) => v.user?.id).filter((v) => !!v) as string[]

    const {
        data: enrollmentData,
        isLoading: enrollmentIsLoading,
        isError: enrollmentIsError,
        error: enrollmentError,
    } = searchAppEntitlementsWithExpiredApi.useSearchAppEntitlementsWithExpired(SearchAppEntitlementsWithExpiredRequest.create({
        appId: appEntitlementValue.appId,
        appEntitlementId: appEntitlementValue.id,
        userRefs: hasMembershipUsers ? currentMembershipPage.map((v) => UserRef.create({
            id: v.user?.id,
        })) : [],
    }), {
        notify: true,
        autoReload: true,
        actionable: hasMembershipUsers,
    })

    const {
        data: tasksData,
        isError: tasksIsError,
        isLoading: tasksIsLoading,
        error: tasksError,
    } = tasksSearchApi.useSearch(
        TaskSearchRequest.create({
            applicationIds: [appEntitlementValue.appId],
            appEntitlementIds: [appEntitlementValue.id],
            taskTypes: [GrantTaskType],
            taskStates: [TaskState.OPEN],
            subjectIds: userIds,
        }),
        {
            pageSize,
            notify: true,
            autoReload: true,
            disableEssentialTableExistenceCheck: true,
            actionable: hasMembershipUsers,
        },
    )

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const appUserEnrollmentStatusMap = useMemo(
        () => enrollmentData[0]?.reduce((acc, v) => {
            const userId = v.user?.id
            const appId = appEntitlementValue.appId
            const appEntitlementId = appEntitlementValue.id

            const status = {
                appEntitlementId: v.appEntitlementId,
                expired: v.expired,
            }

            acc.set(`${userId}:${appId}:${appEntitlementId}:status`, status)

            return acc
        }, new Map<string, EnrollmentStatus>()),
        [enrollmentData, appEntitlementValue.appId, appEntitlementValue.id],
    )

    const appUserTaskMap = useMemo(
        () => tasksData[0]?.reduce((acc, v) => {
            if (v.type?.taskType.oneofKind !== 'grant') return acc

            const appId = appEntitlementValue.appId
            const appEntitlementId = appEntitlementValue.id

            if (v.type?.taskType.oneofKind === 'grant') {
                const userId = v.userId
                acc.set(`${userId}:${appId}:${appEntitlementId}:grant`, v)
            }

            return acc
        }, new Map<string, TaskValue>()),
        [tasksData, appEntitlementValue.appId, appEntitlementValue.id],
    )

    const mergedData = useMemo(() => membershipData.map((p, i) => (i === page
        ? p.map((v) => {
            if (!appUserEnrollmentStatusMap || !appUserTaskMap) {
                return v
            }

            const userId = v.user?.id
            const appId = appEntitlementValue.appId
            const appEntitlementId = appEntitlementValue.id

            const grant = appUserTaskMap?.get(`${userId}:${appId}:${appEntitlementId}:grant`)
            const status = appUserEnrollmentStatusMap?.get(`${userId}:${appId}:${appEntitlementId}:status`)

            return {
                ...v,
                ...(grant && { grant }),
                ...(status && { status }),
            }
        })
        : p
    )), [membershipData, appUserEnrollmentStatusMap, appUserTaskMap, page,
        appEntitlementValue.appId, appEntitlementValue.id,
    ])

    const onReload = searchAppEntitlementsWithExpiredApi
        .useInvalidateSearchAppEntitlementsWithExpired()

    const table = useMemo(() => <C1TableRedux<ProfileEnrollmentAppUsersWithTasksAndExpires, InjectOpenProvisionDialog>
        data={mergedData}
        isError={hasMembershipUsers && (membershipIsError || enrollmentIsError || tasksIsError)}
        isLoading={hasMembershipUsers && (membershipIsLoading || enrollmentIsLoading || tasksIsLoading)}
        error={membershipError || enrollmentError || tasksError}
        canLoadMore={canLoadMore}
        cols={cols}
        onPageChange={onPageChange}
        pageSize={pageSize}
        page={page}
        shouldReload={shouldInvalidateList}
        onReload={onReload}
        staticExtraProps={onProvisionClick}
        emptyState={<EmptyList
            text={<FormattedMessage
                id="components_app_entitlement_user_list_no_members"
                defaultMessage="No members"
            />}
        />}
    />, [
        mergedData, canLoadMore, onPageChange,
        page, shouldInvalidateList, onReload, onProvisionClick,
        enrollmentError, enrollmentIsError, enrollmentIsLoading, membershipError, membershipIsError,
        hasMembershipUsers, membershipIsLoading, tasksError, tasksIsError, tasksIsLoading,
    ])

    if (!hasProvisioningDrawerFF) {
        return null
    }

    return <List<ProfileEnrollmentAppUsersWithTasksAndExpires>
        table={table}
        search={search}
        buttons={<HasFeature feature={FeatureFlagIdGrantReasonsManualMembership}>
            <C1Button variant="outlined" onClick={(): Promise<boolean> => onProvisionAllClick(appEntitlementValue)} sx={{ whiteSpace: 'pre' }}>
                <FormattedMessage defaultMessage="Request for all" id="components_app_entitlement_user_list_enroll_user" />
            </C1Button>
        </HasFeature>}
    />
}
